import { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { LuShieldCheck, LuSparkles } from "react-icons/lu";
import Swal from "sweetalert2";
import { registerUpgradeModal } from "../../../models/upgradeModal";
import { isApp } from "../../../native/platform";
import { manageSubscription } from "../../../models/manageSubscription";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import { active_plan_success } from "../../../actions/activePlan";
import { login_success } from "../../../actions/user";
import { topPlanIdForUser } from "../../../models/planForUser";

// Messages contextualisés selon l'action que le user tentait de faire.
const ACTION_MESSAGES = {
  create_property:
    "Vous avez atteint le nombre maximum de biens autorisé par votre abonnement.",
  off_market: "La vente Off-Market nécessite un abonnement supérieur.",
  open_to_discussion:
    "Le statut OpenToDiscussion nécessite un abonnement supérieur.",
  assistant_ia:
    "L'affectation d'un assistant IA nécessite un abonnement supérieur.",
  dpe_ia: "L'analyse IA du DPE nécessite un abonnement supérieur.",
  campaign: "Vous avez atteint la limite de campagnes Baromètre Prix de votre abonnement.",
  contact_directory:
    "Vous avez atteint le nombre maximum de contacts propriétaires autorisé par votre abonnement.",
};

/**
 * Modale globale déclenchée quand l'utilisateur atteint une limite de son plan
 * ou tente une fonctionnalité non incluse.
 *
 * Deux cas :
 *  1. Le user n'a pas encore consommé d'essai gratuit → proposition d'activer
 *     l'essai gratuit (plan le plus complet de son type, durée définie par le
 *     plan). Gratuit, sans carte bancaire, sans renouvellement automatique.
 *  2. Le user a déjà consommé un essai (ou a un plan payant) → invitation à
 *     faire évoluer son abonnement → /upgrade-plan.
 */
const UpgradePlan = ({ planModal, setplanModal, message = "" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [meta, setMeta] = useState(null);
  const [trialPlan, setTrialPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const hasTrialStarted = Boolean(
    (user?.trialUserForPlan && user?.freeTrialStatus === "done") ||
      user?.planType === "paid"
  );

  useEffect(
    () =>
      registerUpgradeModal((m) => {
        setMeta(m || null);
        setplanModal(true);
      }),
    []
  );

  const topPlanId = topPlanIdForUser(user);

  // Charge le plan d'essai (le plus complet du type de user) pour connaître sa durée.
  useEffect(() => {
    if (!topPlanId || hasTrialStarted) return;
    ApiClient.get("plan/detail", { id: topPlanId })
      .then((res) => {
        if (res?.success && res.data) setTrialPlan(res.data);
      })
      .catch(() => {});
  }, [topPlanId, hasTrialStarted]);

  const contextualMessage =
    meta?.message ||
    (meta?.action ? ACTION_MESSAGES[meta.action] : "") ||
    message ||
    "";

  const trialDays = Number(trialPlan?.trialPeriod) > 0 ? Number(trialPlan.trialPeriod) : 3;

  const startTrial = () => {
    const userId = user?._id || user?.id;
    if (!topPlanId || !userId) return;
    setBusy(true);
    loader(true);
    ApiClient.post("payment/trial/purchase", { userId, planId: topPlanId })
      .then((res) => {
        if (res?.success) {
          setplanModal(false);
          ApiClient.get("user/activeplan", { userId }).then((r) => {
            if (r?.success) dispatch(active_plan_success(r.data));
          });
          ApiClient.get("user/detail", { id: userId }).then((r) => {
            if (r?.success) dispatch(login_success({ ...r.data, loggedIn: true }));
          });
        } else {
          Swal.fire({
            title: t("modals.error", { defaultValue: "Erreur" }),
            text:
              res?.message ||
              t("proPricing.trialFailed", {
                defaultValue: "Impossible de démarrer l'essai gratuit.",
              }),
            icon: "error",
            confirmButtonText: t("buttons.ok", { defaultValue: "OK" }),
          });
        }
      })
      .catch(() =>
        Swal.fire({
          title: t("modals.error", { defaultValue: "Erreur" }),
          text: t("proPricing.trialFailed", {
            defaultValue: "Impossible de démarrer l'essai gratuit.",
          }),
          icon: "error",
          confirmButtonText: t("buttons.ok", { defaultValue: "OK" }),
        })
      )
      .finally(() => {
        setBusy(false);
        loader(false);
      });
  };

  const goUpgrade = () => {
    setplanModal(false);
    navigate("/upgrade-plan");
  };
  const later = () => setplanModal(false);

  const showTrial = !hasTrialStarted && !!topPlanId;

  return (
    <Dialog open={planModal} onClose={later} className="relative z-[9999]">
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="mx-5 w-full max-w-xl rounded-[20px] bg-white p-7">
          {isApp() ? (
            <div className="text-center">
              <DialogTitle as="h3" className="text-[19px] font-extrabold text-[#1F2430]">
                {t("subscription.manage", "Gérer mon abonnement")}
              </DialogTitle>
              <p className="mx-auto mt-3 max-w-md text-[13px] leading-snug text-[#5A6978]">
                {t(
                  "subscription.manageAppNotice",
                  "La gestion de votre abonnement se fait sur le site AnyHomes (hors application)."
                )}
              </p>
              <button
                type="button"
                onClick={() => { setplanModal(false); manageSubscription(navigate); }}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--brand,#976DD0)] px-6 py-3 text-[14px] font-semibold text-white"
              >
                {t("subscription.openWeb", "Ouvrir le site AnyHomes")}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F0E6FF] text-[var(--brand,#976DD0)]">
                  <LuSparkles size={20} />
                </span>
                <div className="min-w-0">
                  <DialogTitle
                    as="h3"
                    className="text-[19px] leading-tight font-extrabold text-[#1F2430]"
                  >
                    {showTrial
                      ? t("upgradeGate.trialTitle", "Débloquez cette fonctionnalité avec un essai gratuit")
                      : t("upgradeGate.evolveTitle", "Faire évoluer votre abonnement pour débloquer la fonctionnalité")}
                  </DialogTitle>
                  {contextualMessage ? (
                    <p className="mt-2 text-[13.5px] leading-snug text-[#5A6978]">
                      {contextualMessage}
                    </p>
                  ) : null}

                  {showTrial ? (
                    <>
                      <p className="mt-2 text-[13.5px] leading-snug text-[#5A6978]">
                        {t(
                          "upgradeGate.trialIntro",
                          "Activez gratuitement votre période d'essai pour accéder à toutes les fonctionnalités de votre profil."
                        )}
                      </p>
                      <div className="mt-3 rounded-2xl border border-[#e9d5ff] bg-[#faf5ff] p-4">
                        <p className="text-[13px] font-semibold text-[#3b2570]">
                          {trialPlan?.name
                            ? `${trialPlan.name}`
                            : t("upgradeGate.trialPlan", "Abonnement complet")}
                          {" · "}
                          <span className="text-[var(--brand,#976DD0)]">
                            {t("upgradeGate.trialDuration", { defaultValue: "{{days}} jours offerts", days: trialDays })}
                          </span>
                        </p>
                        <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[#6B7280]">
                          <span className="inline-flex items-center gap-1.5">
                            <LuShieldCheck size={14} className="text-[var(--brand,#976DD0)]" />
                            {t("upgradeGate.noCard", "Sans carte de crédit")}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <LuShieldCheck size={14} className="text-[var(--brand,#976DD0)]" />
                            {t("upgradeGate.noRenew", "Sans renouvellement automatique")}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <LuShieldCheck size={14} className="text-[var(--brand,#976DD0)]" />
                            {t("upgradeGate.free", "100 % gratuit")}
                          </span>
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="mt-2 text-[13.5px] leading-snug text-[#5A6978]">
                      {t(
                        "upgradeGate.evolveIntro",
                        "Découvrez les offres disponibles et choisissez celle qui correspond à vos besoins."
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={later}
                  className="rounded-full border border-[#d9d9e0] px-5 py-2.5 text-[13.5px] font-semibold text-[#4a4f5c] hover:bg-[#f5f5f7] transition"
                >
                  {t("upgradeGate.later", "Plus tard")}
                </button>
                {showTrial ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={startTrial}
                    className="rounded-full bg-[var(--brand,#976DD0)] px-6 py-2.5 text-[13.5px] font-bold text-white hover:opacity-90 disabled:opacity-60 transition"
                  >
                    {t("upgradeGate.startTrial", "Démarrer mon essai gratuit")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goUpgrade}
                    className="rounded-full bg-[var(--brand,#976DD0)] px-6 py-2.5 text-[13.5px] font-bold text-white hover:opacity-90 transition"
                  >
                    {t("upgradeGate.seePlans", "Voir les abonnements")}
                  </button>
                )}
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default UpgradePlan;
